import { forEach } from 'lodash-es';
import { useCallback } from 'react';
import { matchPath, useNavigate } from 'react-router';

// Utility function to create new path based on pattern and params
function createNewPath(
	pattern: string,
	params: Record<string, string | undefined>
) {
	let newPath = pattern;
	forEach(params, (value, key) => {
		newPath = newPath.replace(`:${key}`, String(value));
	});

	return newPath;
}

export function useTabNavigate() {
	const navigate = useNavigate();

	const navigateToTab = useCallback(
		(tab: string) => {
			const currentPath = window.location.pathname;

			// Determine pattern based on path prefix
			const isBiPath = currentPath.startsWith('/bi/');
			const basePattern = isBiPath ? '/bi/:pagePrefix/:id' : '/:pagePrefix/:id';
			const patternWithTab = `${basePattern}/:tab`;

			// Try to match current path against both patterns
			const match =
				matchPath(basePattern, currentPath) ??
				matchPath(patternWithTab, currentPath);

			if (!match) {
				return;
			}

			// Create new path with the tab
			const newPath = createNewPath(patternWithTab, {
				...match.params,
				tab,
			});

			navigate(newPath);
		},
		[navigate]
	);

	return navigateToTab;
}

export function useListPageTabNavigate() {
	const navigate = useNavigate();

	const navigateToTab = useCallback(
		(tab: string) => {
			const pagePattern = '/:pagePrefix';
			const pagePatternWithTab = `${pagePattern}/:tab`;
			const currentPath = window.location.pathname;
			const match =
				matchPath(pagePattern, currentPath) ??
				matchPath(pagePatternWithTab, currentPath);

			if (!match) {
				return;
			}

			// If the tab is the same as the page prefix (e.g., 'integrations' for '/integrations'),
			// navigate to the base path without adding a tab suffix
			if (tab === match.params.pagePrefix) {
				navigate(`/${match.params.pagePrefix}`);
			} else {
				const newPath = createNewPath(pagePatternWithTab, {
					...match.params,
					tab,
				});
				navigate(newPath);
			}
		},
		[navigate]
	);

	return navigateToTab;
}
